import { createSelector } from 'reselect';
import { chain, isObject } from 'lodash';
import {
    TripType,
    EntryPoint,
    SegmentType,
    ErrorType,
    DpPaxRestrictionPropsDefault,
    SegmentPropDefault,
    DateRangePropDefault,
} from '../../models';
import { ABUtils, Config, DateUtils, DynpackRoomUtils, HotelRoomUtils, WMPUtils } from '../../utils';
import { SearchService } from '../../services';

const getSegmentId = (state, props) => props.segmentId || props.id;
const getIndex = (state, { index }) => index;
const getCurrentSearch = ({ currentSearch }) => currentSearch;
const getPax = ({ currentSearch }) => currentSearch.pax;
const isResident = ({ currentSearch }) => currentSearch.isResident;
const ignoreRoundTripReturnSegment = (tripType, index) => tripType === TripType.ROUND_TRIP && index === 1;

export const getAirlineFilter = ({ currentSearch }) => currentSearch.airlineFilter;
export const getTripType = ({ currentSearch }) => currentSearch.tripType;
export const getTotalSegments = ({ currentSearch }) => currentSearch.segments.length;
export const getRooms = ({ currentSearch }) => HotelRoomUtils.getRoomsAvailableByRestrictions(currentSearch.rooms, DpPaxRestrictionPropsDefault);
export const getSeparateStay = ({ currentSearch }) => currentSearch.separateStay;
export const getSeparateStayEnabled = (state) => getSeparateStay(state).enabled;
export const getSegments = ({ currentSearch }) => currentSearch.segments;
export const getNonPrimeHotelRoom = ({ currentSearch }) => currentSearch.nonPrimeHotelRooms;
export const getActiveProduct = ({ activeProduct }) => activeProduct && activeProduct.activeProduct;
export const getConfig = ({ currentSearch }) => currentSearch.config;
export const isHotel = ({ currentSearch }) => currentSearch && currentSearch.isHotel;
export const isDPProductActive = (store) => getActiveProduct(store) === EntryPoint.DP;
export const isHotelProductActive = (store) => getActiveProduct(store) === EntryPoint.HOTELS;
export const isTrainsProductActive = (store) => getActiveProduct(store) === EntryPoint.TRAINS;
export const isPrime = ({ activeProduct }) => activeProduct && activeProduct.isPrime;

export const hasErrorObject = (error) => isObject(error) && Object.values(error).length > 0;

export const segmentSelectorFactory = (prop) => ({ currentSearch }) =>
    currentSearch.segments.reduce(
        (acc, { id, ...rest }) => ({
            ...acc,
            [id]: rest[prop],
        }),
        {},
    );

// TODO: createSelectors of these functions, and make them depend on getSegments, to avoid pax/direct/class recalculations
const getSegmentDates = segmentSelectorFactory('date');
const getSegmentOrigins = segmentSelectorFactory('origin');
const getSegmentDestinations = segmentSelectorFactory('destination');
const getShowErrors = segmentSelectorFactory('showErrors');

// compute
const validateNotEmpty = ({ type, value }) => {
    return !value ? { type, error: 'empty' } : null;
};

const validateNotIata = ({ type, value }) => {
    return !value.iata ? { type, error: 'empty' } : null;
};

const validateRoundTripDates = (dates) => {
    const isIncomplete = !dates[0] || !dates[1];
    return isIncomplete ? { type: SegmentType.DATE, error: 'incomplete_roundtrip' } : null;
};

export const validateSameCity = ({ origin, destination }) => {
    const isSame = origin && destination && origin.geoNodeId === destination.geoNodeId;
    return isSame ? { type: SegmentType.ARRIVAL, error: 'samecity' } : null;
};

const shouldAlwaysShowError = (error) => {
    return error && error.error === 'samecity';
};

const validateInspirationalEnabled = ({ destination }) => {
    const isInspirational = destination && destination.geoNodeId === 0;
    return isInspirational
        ? { type: SegmentType.ARRIVAL, error: 'inspirational' }
        : null;
};

const validateDynpackDates = ({ dates, isHotel, separateStay }) => {
    let error = null;
    let [date1, date2] = Object.values(dates);
    const areDatesSelected = !!date1 && !!date2;

    if (areDatesSelected && isHotel) {
        const type = SegmentType.DATE;
        const index = 1;
        date1 = DateUtils.stringToDate(date1);
        date2 = DateUtils.stringToDate(date2);
        const daysDiff = DateUtils.getDifferenceInDays(date1, date2);

        if (daysDiff < 1 && (!separateStay.enabled || isHotel)) {
            error = { index, type, error: 'hotels_min_date' };
        } else if (daysDiff > DateUtils.MAX_DAYS_PRIME_HOTEL && !separateStay.enabled) {
            error = { index, type, error: 'hotels_max_date' };
        }
    }
    return error;
};

const getOriginValidation = ({ index, origin, tripType }) => {
    if (ignoreRoundTripReturnSegment(tripType, index)) return null;
    return validateNotEmpty({ type: SegmentType.DEPARTURE, value: origin });
};

export const getFlightDestinationValidation = ({ segment, activeProduct }) => {
    return (
        validateNotEmpty({ type: SegmentType.ARRIVAL, value: segment.destination }) ||
        validateSameCity(segment) ||
        (activeProduct !== EntryPoint.TRAINS
            && validateNotIata({ type: SegmentType.ARRIVAL, value: segment.destination }))
    );
};

const getHotelDestinationValidation = ({ segment }) => {
    return (
        validateNotEmpty({ type: SegmentType.ARRIVAL, value: segment.destination })
        || validateInspirationalEnabled({ destination: segment.destination })
    );
};

const getFlightWithHotelDestinationValidation = ({ segment }) => {
    return (
        validateNotEmpty({ type: SegmentType.ARRIVAL, value: segment.destination })
        || validateSameCity(segment)
        || validateInspirationalEnabled({ destination: segment.destination })
    );
};

const getDateValidation = (date) => validateNotEmpty({ type: SegmentType.DATE, value: date });

const getSeparateStayDateValidation = ({ enabled, checkInDate, checkOutDate }) => {
    let error = null;

    if (enabled && DateUtils.isValidDate(checkInDate) && DateUtils.isValidDate(checkOutDate)) {
        const type = ErrorType.SEPARATE_STAY;
        const index = 1;
        const daysDiff = DateUtils.getDifferenceInDays(checkInDate, checkOutDate);

        if (daysDiff < 1) {
            error = { index, type, error: 'separatestay_min_date' };
        } else if (daysDiff > DateUtils.MAX_DAYS_PRIME_HOTEL) {
            error = { index, type, error: 'separatestay_max_date' };
        }
    }
    return error;
};

const getRegionsSegment = ({ origin, destination }) => {
    let regions = [];
    const residentZones = Config.get('residentZones');
    const residentZonesParsed = residentZones ? JSON.parse(residentZones) : [];
    if (origin && destination && origin.countryCode === destination.countryCode) {
        if (residentZonesParsed.hasOwnProperty(origin.iata)) {
            regions = regions.concat(residentZonesParsed[origin.iata]);
        }

        if (residentZonesParsed.hasOwnProperty(destination.iata)) {
            regions = regions.concat(residentZonesParsed[destination.iata]);
        }
    }
    return regions;
};

// selectors
export const getSegmentById = createSelector(
    [getSegments, getSegmentId],
    (segments, id) =>
        segments.find((segment) => segment.id === id),
);

export const isInspirationalSearch = createSelector([getCurrentSearch], (currentSearch) =>
    SearchService.isInspirational(currentSearch),
);

export const isMultipleSegments = createSelector(
    [getTripType, getTotalSegments],
    (tripType, totalSegments) => {
        return (
            tripType === TripType.MULTIPLE || (tripType === TripType.ONE_WAY && totalSegments > 1)
        );
    },
);

export const totalPax = createSelector([getPax, isHotel, isPrime, getNonPrimeHotelRoom], (pax, isHotel, isPrime, nonPrimeHotelRooms) =>
    ( isHotel && !isPrime) ? Object.values(nonPrimeHotelRooms).reduce((a, b) => a + b) :
        Object.values(pax).reduce((a, b) => a + b),
);

const getRegions = createSelector(
    [getSegments, isMultipleSegments],
    (segments, multiple) => {
        const filter = () =>
            chain(segments)
                .map((segment) => getRegionsSegment(segment))
                .flatten()
                .uniq()
                .value();

        return multiple ? [] : filter();
    },
);

export const getResidentDiscountProps = createSelector(
    [getPax, isResident, getRegions],
    (pax, checked, regions) => {
        const hasNonAdults = pax.kids > 0 || pax.infants > 0;

        return {
            checked,
            regions,
            show: !!regions.length,
            hasNonAdults,
        };
    },
);

export const getDynpackDatesErrorSelector = createSelector(
    [getSegmentDates, isHotel, isHotelProductActive, getSeparateStay],
    (dates, hotel, isHotelProductActive, separateStay) => validateDynpackDates({ dates, isHotel: (hotel || isHotelProductActive), separateStay }),
);

const getDateErrorsSelector = createSelector(
    [getSegmentDates, getDynpackDatesErrorSelector],
    (segmentDates, dynpackDatesError) => {
        const result = {};

        Object.keys(segmentDates).forEach((key, index) => {
            let error = getDateValidation(segmentDates[key]);

            if (index === 1 && !error) {
                error = dynpackDatesError;
            }

            if (error) {
                error.index = index;
                result[key] = error;
            }
        });

        return result;
    },
);

const getDateFirstErrorSelector = createSelector(
    [getDateErrorsSelector, getShowErrors],
    (dateErrors, display) => {
        return (
            Object.entries(dateErrors)
                // only the errors that should be displayed
                .filter(([key]) => display[key])
                // get the first
                .reduce((acc, [, value]) => acc || value, null)
        );
    },
);

export const getRoundTripDateErrorsSelector = createSelector(
    [getSegments, getDateFirstErrorSelector],
    (segments, errors) => {
        const dates = segments
            .slice(0, 2)
            .filter((s) => s.showErrors)
            .map((s) => s.date);
        return dates.length ? validateRoundTripDates(dates) || errors : null;
    },
);

const getOriginErrorsSelector = createSelector(
    [getSegmentOrigins, getTripType],
    (segmentOrigins, tripType) => {
        const result = {};

        Object.keys(segmentOrigins).forEach((key, index) => {
            const error = getOriginValidation({ index, origin: segmentOrigins[key], tripType });

            if (error) {
                error.index = index;
                result[key] = error;
            }
        });

        return result;
    },
);

const getHotelsDestinationErrorsSelector = createSelector(
    [getSegmentDestinations, getSegmentOrigins],
    // eslint-disable-next-line max-params
    (segmentDestinations, segmentOrigins) => {
        const result = {};

        Object.keys(segmentDestinations).forEach((key, index) => {
            const segment = {
                origin: segmentOrigins[key],
                destination: segmentDestinations[key],
            };

            const error = index === 0 && getHotelDestinationValidation({ segment });

            if (error) {
                error.index = index;
                result[key] = error;
            }
        });

        return result;
    },
);

const getDestinationErrorsSelector = createSelector(
    [getSegmentDestinations, getSegmentOrigins, isHotel, getTripType, getActiveProduct],
    // eslint-disable-next-line max-params
    (segmentDestinations, segmentOrigins, isHotel, tripType, activeProduct) => {
        const result = {};

        Object.keys(segmentDestinations).forEach((key, index) => {
            const segment = {
                origin: segmentOrigins[key],
                destination: segmentDestinations[key],
            };

            if (ignoreRoundTripReturnSegment(tripType, index)) return null;

            const error = isHotel
                ? getFlightWithHotelDestinationValidation({ segment })
                : getFlightDestinationValidation({ segment, activeProduct });

            if (error) {
                error.index = index;
                result[key] = error;
            }
        });

        return result;
    },
);

const prop = (key, obj) => obj[key];

const shouldDisplay = (key, obj, display) =>
    display[key] || shouldAlwaysShowError(obj[key]) ? obj[key] : null;

export const getOriginSelector = createSelector([getSegmentId, getSegmentOrigins], prop);

export const getDestinationSelector = createSelector([getSegmentId, getSegmentDestinations], prop);

export const getOriginErrorSelector = createSelector(
    [getSegmentId, getOriginErrorsSelector, getShowErrors],
    shouldDisplay,
);

export const getDestinationErrorSelector = createSelector(
    [getSegmentId, getDestinationErrorsSelector, getShowErrors],
    shouldDisplay,
);

export const getDestinationHotelErrorSelector = createSelector(
    [getSegmentId, getHotelsDestinationErrorsSelector, getShowErrors],
    shouldDisplay,
);

export const getDateErrorSelector = createSelector(
    [getSegmentId, getDateErrorsSelector, getShowErrors],
    shouldDisplay,
);

const getSegmentsErrorsSelector = createSelector(
    [getDateErrorsSelector, getOriginErrorsSelector, getDestinationErrorsSelector],
    (datesErrors, originsErrors, destinationsErrors) => {
        return []
            .concat(Object.values(datesErrors))
            .concat(Object.values(originsErrors))
            .concat(Object.values(destinationsErrors));
    },
);

const getHotelsSegmentsErrorsSelector = createSelector(
    [getDateErrorsSelector, getHotelsDestinationErrorsSelector],
    (datesErrors, destinationsErrors) => {
        return []
            .concat(Object.values(datesErrors))
            .concat(Object.values(destinationsErrors));
    },
);

const getAgeKidsErrorSelector = createSelector([getRooms, isHotel, isPrime, getNonPrimeHotelRoom, isDPProductActive],
    (rooms, isHotel, isPrime, nonPrimeHotelRoom, isDP) => {
        if ((isHotel && isPrime) || isDP) return HotelRoomUtils.getAgeKidsValidation(rooms);
        else if (isHotel && !isPrime) return DynpackRoomUtils.getNonPrimeAgeKidsValidation(nonPrimeHotelRoom);
        else return null;
    }
);

export const hasAgeKidsErrors = createSelector(
    [getAgeKidsErrorSelector],
    hasErrorObject
);

const getSeparateStayErrorsSelector = createSelector([getSeparateStay], (separateStay) => ({
    0: null,
    1: getSeparateStayDateValidation(separateStay),
}));

export const getSeparateStayErrorSelector = createSelector(
    [getIndex, getSeparateStayErrorsSelector],
    prop,
);

export const getValidationErrorsSelector = createSelector(
    [getSegmentsErrorsSelector, getAgeKidsErrorSelector, getSeparateStayErrorsSelector],
    (segmentErrors, ageKidsErrors, separateStayErrors) => {
        return []
            .concat(segmentErrors)
            .concat(ageKidsErrors)
            .concat(Object.values(separateStayErrors))
            .filter(hasErrorObject);
    },
);

export const getValidationErrorsSelectorHotel = createSelector(
    [getHotelsSegmentsErrorsSelector, getAgeKidsErrorSelector],
    (segmentErrors, ageKidsErrors) => {
        return []
            .concat(segmentErrors)
            .concat(ageKidsErrors)
            .filter(hasErrorObject);
    },
);

export const showKidsErrors = createSelector(
    [getShowErrors, hasAgeKidsErrors],
    (showErrors, hasError) => hasError && Object.values(showErrors).some((showError) => showError),
);

export const hasKidsErrors = createSelector(
    [getRooms],
    (rooms) => rooms.some((room) => room.showKidError),
);

export const hasNonPrimeHotelKidsErrors = createSelector(
    [getNonPrimeHotelRoom],
    (nonPrimeHotelRoom) => nonPrimeHotelRoom.showKidError ?? false,
);

export const showKNonPrimeHotelKidsErrors = createSelector(
    [getNonPrimeHotelRoom, hasNonPrimeHotelKidsErrors],
    (nonPrimeHotelRoom, hasError) => {
        if (nonPrimeHotelRoom.showKidError) {
            return hasError && nonPrimeHotelRoom.kids.some((age) => age === -1);
        } else return false;
    },
);

export const isFlightValidSearch = createSelector(
    [getValidationErrorsSelector],
    (validationErrors) => !validationErrors.length,
);

export const isHotelValidSearch = createSelector(
    [getValidationErrorsSelectorHotel],
    (validationErrors) => !validationErrors.length,
);

export const isCurrentSearchPopulated = createSelector([getCurrentSearch], (currentSearch) => {
    return currentSearch.segments.some((segment) => {
        return 'date' in segment || 'origin' in segment || 'destination' in segment;
    });
});
export const shouldShowSeparateStay = createSelector([getSegmentDates], (dates) => {
    const ids = Object.keys(dates);
    const parsedDate0 = DateUtils.stringToDate(dates[ids[0]]);
    const parsedDate1 = DateUtils.stringToDate(dates[ids[1]]);
    return (
        ids.length === 2 &&
        DateUtils.isValidDate(parsedDate0) &&
        DateUtils.isValidDate(parsedDate1) &&
        DateUtils.getDifferenceInDays(parsedDate0, parsedDate1) !== 0
    );
});

export const canAddRoom = createSelector(
    [getRooms],
    (rooms) => HotelRoomUtils.hasAvailableRoomLimitation(rooms, DpPaxRestrictionPropsDefault)
);

export const isEditReturn = createSelector(
    [getTripType, getSegments],
    (tripType, segments) =>
        tripType === TripType.ROUND_TRIP && !!segments[0].date && !segments[1].date,
);

export const displayFaresInCalendar = createSelector(
    [getActiveProduct, getTripType, getSegments],
    (activeProduct, tripType, segments) => {
        const { isPricesInCalendarActive } = ABUtils.getABs();
        const isNotMultiple =
            tripType === TripType.ROUND_TRIP ||
            (tripType === TripType.ONE_WAY && segments.length === 1);
        const isLocationsFilled = !!segments[0].origin && !!segments[0].destination;
        return isPricesInCalendarActive && activeProduct === EntryPoint.FLIGHTS && isNotMultiple && isLocationsFilled;
    },
);

export const getAdultsAndKids = createSelector(
    [getRooms],
    (rooms) => HotelRoomUtils.getTotalAdultsAndKids(rooms, DpPaxRestrictionPropsDefault),
);

export const getAdultsAndKidsNonPrimeHotels = createSelector(
    [getNonPrimeHotelRoom],
    (nonPrimeHotelRoom) => nonPrimeHotelRoom ? { adults: nonPrimeHotelRoom.adults,
        kids: nonPrimeHotelRoom.kids.length, rooms: nonPrimeHotelRoom.rooms } :
        { adults: 0, kids: [], rooms: 0 },
);

export const getSegmentConfig = createSelector(
    [getSegments, getSegmentId, getConfig],
    (segments, id, config) => {
        const index = segments.findIndex((segment) => segment.id === id);
        return config?.segments[index] || SegmentPropDefault;
    },
);

export const getDateRangeConfig = createSelector(
    [getSegments, getSegmentId, getConfig],
    (segments, id, config) => {
        const index = segments.findIndex((segment) => segment.id === id);
        return config?.dateRange[index] || DateRangePropDefault;
    },
);

export const isStopoverMode = createSelector(
    [getTripType],
    (tripType) => tripType === WMPUtils.getTripType() && !!WMPUtils.getStopoverConfig(),
);
